/**
 * ResizableImage — extends TipTap's `@tiptap/extension-image` with a custom
 * React NodeView that renders corner resize handles when the image node is
 * selected. Dragging a handle adjusts the node's `width` attribute (already
 * part of the base Image extension's schema, default: null), which TipTap
 * serializes into the generated HTML (`getHTML()`) as the `width` attribute
 * on the `<img>` tag — no extra `addAttributes` override needed.
 *
 * No new dependency: built on `@tiptap/extension-image` + `@tiptap/react`
 * (both already installed for the shared RichTextEditor).
 */

import React, { useCallback, useEffect, useRef } from 'react';
import TiptapImage from '@tiptap/extension-image';
import { NodeViewWrapper, ReactNodeViewRenderer, type NodeViewProps } from '@tiptap/react';
import type { EditorView } from '@tiptap/pm/view';

const MIN_WIDTH = 60;

type ResizeCorner = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

interface DragState {
  corner: ResizeCorner;
  startX: number;
  startWidth: number;
  aspectRatio: number;
}

const CORNERS: ResizeCorner[] = ['top-left', 'top-right', 'bottom-left', 'bottom-right'];

const handleStyle = (corner: ResizeCorner): React.CSSProperties => ({
  position: 'absolute',
  width: 10,
  height: 10,
  borderRadius: '50%',
  backgroundColor: 'var(--theme-primary)',
  border: '2px solid #ffffff',
  boxShadow: '0 0 2px rgba(0, 0, 0, 0.35)',
  cursor: corner === 'top-left' || corner === 'bottom-right' ? 'nwse-resize' : 'nesw-resize',
  top: corner.startsWith('top') ? -5 : undefined,
  bottom: corner.startsWith('bottom') ? -5 : undefined,
  left: corner.endsWith('left') ? -5 : undefined,
  right: corner.endsWith('right') ? -5 : undefined,
});

/**
 * Inserts an image node directly via the ProseMirror view (used from
 * editorProps.handlePaste/handleDrop, where the higher-level `editor` chain
 * API instance isn't safely referenceable — those handlers are defined
 * inline inside the same `useEditor()` call that produces `editor`).
 */
export function insertResizableImageAt(view: EditorView, src: string, pos?: number): void {
  const { schema } = view.state;
  const imageType = schema.nodes.image;
  if (!imageType) return;
  const node = imageType.create({ src });
  const tr = typeof pos === 'number' ? view.state.tr.insert(pos, node) : view.state.tr.replaceSelectionWith(node);
  view.dispatch(tr);
  view.focus();
}

const ResizableImageView: React.FC<NodeViewProps> = ({ node, updateAttributes, selected }) => {
  const imgRef = useRef<HTMLImageElement | null>(null);
  const dragState = useRef<DragState | null>(null);

  // Keep the latest updateAttributes in a ref so handlePointerUp stays
  // referentially stable across renders (window listeners added on drag start
  // must be removable with the exact same function reference).
  const updateAttributesRef = useRef(updateAttributes);
  updateAttributesRef.current = updateAttributes;

  const handlePointerMove = useCallback((event: PointerEvent) => {
    const state = dragState.current;
    const img = imgRef.current;
    if (!state || !img) return;
    const delta = event.clientX - state.startX;
    const direction = state.corner === 'top-left' || state.corner === 'bottom-left' ? -1 : 1;
    const nextWidth = Math.max(MIN_WIDTH, Math.round(state.startWidth + delta * direction));
    img.style.width = `${nextWidth}px`;
    img.style.height = `${Math.round(nextWidth / state.aspectRatio)}px`;
  }, []);

  const handlePointerUp = useCallback(() => {
    window.removeEventListener('pointermove', handlePointerMove);
    window.removeEventListener('pointerup', handlePointerUp);
    const img = imgRef.current;
    if (dragState.current && img) {
      const finalWidth = Math.round(img.getBoundingClientRect().width);
      // `height: null` clears any legacy fixed height attribute so the
      // committed `width` + intrinsic aspect ratio govern the rendered size
      // (otherwise a stale height would distort the image after resize).
      updateAttributesRef.current({ width: finalWidth, height: null });
    }
    dragState.current = null;
  }, [handlePointerMove]);

  // If the component unmounts mid-drag (e.g. node deleted, editor destroyed),
  // remove the window listeners and reset the drag state.
  useEffect(() => {
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      dragState.current = null;
    };
  }, [handlePointerMove, handlePointerUp]);

  const startResize = useCallback(
    (corner: ResizeCorner) => (event: React.PointerEvent<HTMLSpanElement>) => {
      event.preventDefault();
      event.stopPropagation();
      const img = imgRef.current;
      if (!img) return;
      const rect = img.getBoundingClientRect();
      const naturalAspect =
        img.naturalWidth && img.naturalHeight
          ? img.naturalWidth / img.naturalHeight
          : rect.width && rect.height
            ? rect.width / rect.height
            : 1;
      dragState.current = {
        corner,
        startX: event.clientX,
        startWidth: rect.width,
        aspectRatio: naturalAspect || 1,
      };
      window.addEventListener('pointermove', handlePointerMove);
      window.addEventListener('pointerup', handlePointerUp);
    },
    [handlePointerMove, handlePointerUp]
  );

  const widthAttr: unknown = node.attrs.width;
  const width =
    typeof widthAttr === 'number'
      ? widthAttr
      : typeof widthAttr === 'string' && widthAttr
        ? Number(widthAttr)
        : undefined;

  return (
    <NodeViewWrapper
      className="richtext-resizable-image"
      style={{ display: 'inline-block', position: 'relative', maxWidth: '100%', lineHeight: 0 }}
      data-testid="richtext-image-wrapper"
    >
      <img
        ref={imgRef}
        src={node.attrs.src as string}
        alt={(node.attrs.alt as string) || ''}
        title={(node.attrs.title as string) || undefined}
        draggable={false}
        style={{
          width: width ? `${width}px` : undefined,
          maxWidth: '100%',
          height: 'auto',
          display: 'block',
          borderRadius: 4,
          outline: selected ? '2px solid var(--theme-primary)' : 'none',
          outlineOffset: 2,
        }}
        data-testid="richtext-image"
      />
      {selected &&
        CORNERS.map((corner) => (
          <span
            key={corner}
            draggable={false}
            onDragStart={(event) => event.preventDefault()}
            onPointerDown={startResize(corner)}
            style={handleStyle(corner)}
            data-testid={`richtext-image-resize-handle-${corner}`}
          />
        ))}
    </NodeViewWrapper>
  );
};

export const ResizableImage = TiptapImage.extend({
  addNodeView() {
    return ReactNodeViewRenderer(ResizableImageView);
  },
});
